import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { TextInputField } from '@sixui/ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';

const PASSWORD_MIN_LENGTH = 6;

export type IUserPasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
};

export type IUserPasswordFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IUserPasswordFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<IUserPasswordFormValues>;
    onSubmit?: SubmitHandler<IUserPasswordFormValues>;
  };

export const UserPasswordForm: React.FC<IUserPasswordFormProps> = (props) => {
  const { sx, defaultValues, onSubmit, ...other } = props;
  const { t } = useTranslation();

  const form = useForm<IUserPasswordFormValues>({ defaultValues });

  return (
    <ContentBlock.FormBody sx={sx} form={form} onSubmit={onSubmit} {...other}>
      <FormField
        as={TextInputField}
        forwardError
        control={form.control}
        name="currentPassword"
        type="password"
        label={t('UserPasswordForm.current-password')}
        rules={{ required: true }}
      />
      <FormField
        as={TextInputField}
        forwardError
        control={form.control}
        name="newPassword"
        type="password"
        label={t('UserPasswordForm.new-password')}
        rules={{
          required: true,
          minLength: {
            value: PASSWORD_MIN_LENGTH,
            message: t('ResetPasswordFormMainSection.password-too-short', {
              minLength: PASSWORD_MIN_LENGTH,
            }),
          },
          validate: (value) => {
            if (form.watch('currentPassword') === value) {
              return t(
                'UserPasswordForm.the-new-password-must-be-different-from-the-current-one',
              );
            }
          },
        }}
      />
      <FormField
        as={TextInputField}
        forwardError
        control={form.control}
        name="newPasswordConfirmation"
        type="password"
        label={t('UserPasswordForm.confirm-new-password')}
        rules={{
          required: true,
          validate: (value) => {
            if (form.watch('newPassword') !== value) {
              return t('UserPasswordForm.passwords-dont-match');
            }
          },
        }}
      />
    </ContentBlock.FormBody>
  );
};
